import {
  ASSIGNEE_KIND_AGENT,
  ASSIGNEE_KIND_CHATBOT,
  ASSIGNEE_KIND_USER,
  buildAssigneeOption,
  getAgentId,
  isChatBotUser,
  resolveUserKind,
} from "./assigneeTransferField";

export const ASSIGNEE_FILTER_FIELD = "responsavel";
export const LEGACY_CONDITION_USER_KEY = "usuarios";
export const LEGACY_CONDITION_AGENT_KEY = "agentesdeia";

const TOKEN_SEP = ":";

export function assigneeKindToTokenPrefix(kind) {
  if (kind === ASSIGNEE_KIND_AGENT) return "agent";
  if (kind === ASSIGNEE_KIND_CHATBOT) return "chatbot";
  return "user";
}

export function parseAssigneeFilterToken(token) {
  const raw = String(token || "").trim();
  if (!raw.includes(TOKEN_SEP)) return null;
  const idx = raw.indexOf(TOKEN_SEP);
  const kind = raw.slice(0, idx).trim().toLowerCase();
  const id = raw.slice(idx + 1).trim();
  if (!id) return null;
  if (kind === "agent") return { kind: ASSIGNEE_KIND_AGENT, id };
  if (kind === "chatbot") return { kind: ASSIGNEE_KIND_CHATBOT, id: Number(id) };
  if (kind === "user") return { kind: ASSIGNEE_KIND_USER, id: Number(id) };
  return null;
}

export function assigneeSelectionToFilterTokens(selection = []) {
  return (Array.isArray(selection) ? selection : [])
    .filter(Boolean)
    .map((item) => `${assigneeKindToTokenPrefix(item.kind)}:${item.id}`);
}

/** Lê condições salvas (novo `responsavel` ou legado `usuarios` / `agentesdeia`). */
export function condicoesToAssigneeSelection(values = {}, { users = [], agents = [] } = {}) {
  const tokens = values?.[ASSIGNEE_FILTER_FIELD];
  if (Array.isArray(tokens) && tokens.length) {
    return tokens
      .map((token) => {
        const parsed = parseAssigneeFilterToken(token);
        if (!parsed) return null;
        if (parsed.kind === ASSIGNEE_KIND_AGENT) {
          const agent = (agents || []).find((a) => String(getAgentId(a)) === String(parsed.id));
          return buildAssigneeOption({
            kind: ASSIGNEE_KIND_AGENT,
            id: parsed.id,
            label: agent?.name ?? agent?.nome ?? `Agente #${parsed.id}`,
            raw: agent,
          });
        }
        const numId = Number(parsed.id);
        const user = (users || []).find((u) => Number(u.id) === numId);
        const kind = parsed.kind === ASSIGNEE_KIND_CHATBOT
          ? ASSIGNEE_KIND_CHATBOT
          : resolveUserKind(user, numId);
        const name = user?.nome ?? user?.name ?? `#${numId}`;
        return buildAssigneeOption({
          kind,
          id: numId,
          label:
            kind === ASSIGNEE_KIND_CHATBOT && !String(name).endsWith("(ChatBot)")
              ? `${name} (ChatBot)`
              : name,
          raw: user,
        });
      })
      .filter(Boolean);
  }

  const out = [];
  const legacyUsers = values?.[LEGACY_CONDITION_USER_KEY];
  const userIds = Array.isArray(legacyUsers)
    ? legacyUsers
    : legacyUsers != null && legacyUsers !== ""
    ? [legacyUsers]
    : [];

  for (const id of userIds) {
    const numId = Number(id);
    if (!Number.isFinite(numId) || numId <= 0) continue;
    const user = (users || []).find((u) => Number(u.id) === numId);
    const kind = resolveUserKind(user, numId);
    const name = user?.nome ?? user?.name ?? `Usuário #${numId}`;
    const opt = buildAssigneeOption({
      kind,
      id: numId,
      label: kind === ASSIGNEE_KIND_CHATBOT && !String(name).endsWith("(ChatBot)") ? `${name} (ChatBot)` : name,
      raw: user,
    });
    if (opt) out.push(opt);
  }

  const legacyAgents = values?.[LEGACY_CONDITION_AGENT_KEY];
  const agentIds = Array.isArray(legacyAgents)
    ? legacyAgents
    : legacyAgents != null && legacyAgents !== ""
    ? [legacyAgents]
    : [];

  for (const id of agentIds) {
    const aid = String(id).trim();
    if (!aid) continue;
    const agent = (agents || []).find((a) => String(getAgentId(a)) === aid);
    const opt = buildAssigneeOption({
      kind: ASSIGNEE_KIND_AGENT,
      id: aid,
      label: agent?.name ?? agent?.nome ?? `Agente #${aid}`,
      raw: agent,
    });
    if (opt) out.push(opt);
  }

  return out;
}

export function assigneeSelectionToCondicoesPatch(selection = []) {
  const tokens = assigneeSelectionToFilterTokens(selection);
  return {
    [ASSIGNEE_FILTER_FIELD]: tokens,
    [LEGACY_CONDITION_USER_KEY]: "",
    [LEGACY_CONDITION_AGENT_KEY]: "",
  };
}

/** Filtro de condição: permite vários de qualquer tipo (OR na avaliação). */
export function normalizeAssigneeFilterSelection(nextSelection = []) {
  const seen = new Set();
  const out = [];
  for (const item of Array.isArray(nextSelection) ? nextSelection : []) {
    if (!item?.kind || item.id == null) continue;
    const key = `${item.kind}:${item.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

export function assigneeFilterSelectionLabels(selection = []) {
  const list = Array.isArray(selection) ? selection : [];
  if (!list.length) return "";
  return list.map((item) => item.label).filter(Boolean).join(", ");
}

export function hasLegacyAssigneeConditionValues(values = {}) {
  const u = values?.[LEGACY_CONDITION_USER_KEY];
  const a = values?.[LEGACY_CONDITION_AGENT_KEY];
  const hasUsers =
    u != null && u !== "" && (!Array.isArray(u) || u.length > 0);
  const hasAgents =
    a != null && a !== "" && (!Array.isArray(a) || a.length > 0);
  return hasUsers || hasAgents;
}

/** Converte valores legados em tokens `responsavel` (sem depender de catálogo carregado). */
export function legacyCondicoesToResponsavelTokens(values = {}) {
  const tokens = [];
  const legacyUsers = values?.[LEGACY_CONDITION_USER_KEY];
  const userIds = Array.isArray(legacyUsers)
    ? legacyUsers
    : legacyUsers != null && legacyUsers !== ""
    ? [legacyUsers]
    : [];
  for (const id of userIds) {
    const num = Number(id);
    if (Number.isFinite(num) && num > 0) tokens.push(`user:${num}`);
  }

  const legacyAgents = values?.[LEGACY_CONDITION_AGENT_KEY];
  const agentIds = Array.isArray(legacyAgents)
    ? legacyAgents
    : legacyAgents != null && legacyAgents !== ""
    ? [legacyAgents]
    : [];
  for (const id of agentIds) {
    const aid = String(id).trim();
    if (aid) tokens.push(`agent:${aid}`);
  }
  return tokens;
}

/**
 * Normaliza condições salvas: `usuarios` / `agentesdeia` → `responsavel` quando o schema usa assignee_filter.
 */
export function normalizeCondicoesAssigneeFilter(condicoes = {}, eventSchema = {}) {
  if (!eventSchema?.[ASSIGNEE_FILTER_FIELD]) return condicoes;

  const hasNew =
    Array.isArray(condicoes?.[ASSIGNEE_FILTER_FIELD]) &&
    condicoes[ASSIGNEE_FILTER_FIELD].length > 0;

  if (hasNew) {
    if (!hasLegacyAssigneeConditionValues(condicoes)) return condicoes;
    return {
      ...condicoes,
      [LEGACY_CONDITION_USER_KEY]: "",
      [LEGACY_CONDITION_AGENT_KEY]: "",
    };
  }

  if (!hasLegacyAssigneeConditionValues(condicoes)) return condicoes;

  return {
    ...condicoes,
    [ASSIGNEE_FILTER_FIELD]: legacyCondicoesToResponsavelTokens(condicoes),
    [LEGACY_CONDITION_USER_KEY]: "",
    [LEGACY_CONDITION_AGENT_KEY]: "",
  };
}
